import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageProvider";
import { useAuth } from "../state/AuthContext";
import { Alert, Spinner } from "./ui";

/**
 * Client-side route guards.
 *
 * These exist for user experience only -- to avoid rendering a screen that
 * would immediately fail, and to send people to sign-in with a return
 * path. They are not access control. Every protected read and write is
 * authorised again on the server, so bypassing these in the browser gains
 * nothing.
 */

export const RequireAuth = () => {
  const { status } = useAuth();
  const { path } = useLanguage();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="gw-container gw-section">
        <Spinner />
      </div>
    );
  }

  if (status === "signed-out") {
    // Remember where they were headed so sign-in can return them there.
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${path("/sign-in")}?next=${next}`} replace />;
  }

  return <Outlet />;
};

/**
 * Requires a verified email address.
 *
 * Applies to submitting an application and to recording, matching the
 * server-side rule. Rather than blocking with a dead end, it redirects to
 * the verification screen where the user can resend the email.
 */
export const RequireVerifiedEmail = () => {
  const { status, emailVerified } = useAuth();
  const { path } = useLanguage();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="gw-container gw-section">
        <Spinner />
      </div>
    );
  }

  if (status === "signed-out") {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${path("/sign-in")}?next=${next}`} replace />;
  }

  if (!emailVerified) {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${path("/verify-email")}?next=${next}`} replace />;
  }

  return <Outlet />;
};

export const RequireAdmin = () => {
  const { status, isAdmin, profile } = useAuth();
  const { t, path } = useLanguage();
  const location = useLocation();

  if (status === "loading" || (status === "signed-in" && profile === null)) {
    return (
      <div className="gw-container gw-section">
        <Spinner />
      </div>
    );
  }

  if (status === "signed-out") {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${path("/sign-in")}?next=${next}`} replace />;
  }

  if (!isAdmin) {
    // A plain explanation rather than a redirect: silently bouncing an
    // authenticated user is confusing, and the admin API would refuse them
    // regardless.
    return (
      <div className="gw-container gw-section">
        <Alert tone="danger" title={t("admin.notAdminTitle")}>
          {t("admin.notAdminBody")}
        </Alert>
      </div>
    );
  }

  return <Outlet />;
};
